import { Map as MapIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ContinueCard } from "@/components/skills/ContinueCard";
import { LockedExplainSheet } from "@/components/skills/LockedExplainSheet";
import { ScenarioRow } from "@/components/skills/ScenarioRow";
import { StallScreen } from "@/components/skills/StallScreen";
import { useMe } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { type ScenarioSummary, countMastered, isStallCondition, useScenarios } from "@/lib/scenarios";

// ─── Skills home (Journal of capabilities) ───────────────────────
//
// `scenarios.md` §8.1 + `scenarios-phase4.md` deliverable §1.
// Read-only browse: hero counter + "Continue" card if any scenario
// is in flight + grouped lists by status.
//
// Phase 4 keeps this strictly read-only — all CTAs navigate to
// Detail. Phase 5 wires the actual Session flow on top.

export function SkillsPage() {
  const t = useT();
  const { data: user } = useMe();
  const scenarios = useScenarios();
  // Locked-explain bottom sheet: opens with a scenario when the
  // user taps a LOCKED row. Null = closed.
  const [lockedFocus, setLockedFocus] = useState<ScenarioSummary | null>(null);

  const sections = useMemo(() => groupByStatus(scenarios.data ?? []), [scenarios.data]);
  const mastered = countMastered(scenarios.data);
  const total = scenarios.data?.length ?? 0;
  const level = user?.currentLevel ?? "A1";
  // Stall condition: nothing actionable in journal — replace the
  // sections list with the StallScreen so the user sees what to do.
  const isStalled = isStallCondition(scenarios.data);

  return (
    <div className="flex flex-col gap-4">
      <Header level={level} />

      <CapabilityHero mastered={mastered} total={total} level={level} />

      {sections.continueScenario && <ContinueCard scenario={sections.continueScenario} />}

      {isStalled ? <StallScreen /> : <Sections sections={sections} onLockedTap={setLockedFocus} />}

      {scenarios.data && scenarios.data.length === 0 && <EmptyState message={t("skills.empty_no_scenarios")} />}

      <div className="mt-2 flex justify-center">
        <Link
          to="/skills/tree"
          className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-text-3 transition-colors hover:bg-[color:var(--surface-2)]"
        >
          <MapIcon className="size-3" /> {t("skills.tree_journal_link")}
        </Link>
      </div>

      <LockedExplainSheet
        scenario={lockedFocus}
        onClose={() => setLockedFocus(null)}
        // Chain into the prereq's own LockedExplain when the user
        // taps a still-LOCKED dependency — same sheet, swapped focus.
        onFocus={(s) => setLockedFocus(s)}
      />
    </div>
  );
}

function Header({ level }: { level: string }) {
  const t = useT();
  return (
    <div className="flex items-center justify-between">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-text-3">{level} · skills</div>
        <h1 className="text-[22px] font-bold tracking-tight">{t("skills.title")}</h1>
      </div>
    </div>
  );
}

function CapabilityHero({ mastered, total, level }: { mastered: number; total: number; level: string }) {
  const t = useT();
  // Tick bar grouped 6×10 — readable proxy for progress without
  // becoming a single thin line. See mockup `scenarios.jsx` line ~255.
  // For totals < 60 we still render 60 ticks; some are just always
  // empty. This is intentional: the hero anchors expectation
  // ("how many capabilities exist at this level"), not just
  // "what fraction of authored scenarios I've done".
  const TICK_GROUPS = 6;
  const TICKS_PER_GROUP = 10;
  const totalTicks = TICK_GROUPS * TICKS_PER_GROUP;

  return (
    <div
      className="rounded-[var(--r-lg)] p-[18px] shadow-[var(--shadow-sm)]"
      style={{
        background: "linear-gradient(140deg, var(--scen-bg2) 0%, var(--surface) 75%)",
      }}
    >
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--scen-fg)" }}>
        {t("skills.hero_label")}
      </div>
      <div className="mb-3.5 flex items-baseline gap-2">
        <span className="text-[44px] font-bold leading-none tracking-tight" style={{ color: "var(--scen-ink)" }}>
          {mastered}
        </span>
        <span className="text-[14px] text-text-3">{t("skills.hero_count_of", { total: total.toString(), level })}</span>
      </div>
      <div className="flex gap-1.5">
        {Array.from({ length: TICK_GROUPS }).map((_, gi) => (
          <div key={gi} className="flex flex-1 gap-[2px]">
            {Array.from({ length: TICKS_PER_GROUP }).map((__, ti) => {
              const i = gi * TICKS_PER_GROUP + ti;
              const filled = i < mastered;
              const upcoming = i >= mastered && i < Math.min(mastered + 3, total);
              return (
                <div
                  key={ti}
                  className="h-1.5 flex-1 rounded-[1.5px]"
                  style={{
                    background: filled ? "var(--scen-solid)" : upcoming ? "var(--scen-bg2)" : "var(--surface-3)",
                    visibility: i < totalTicks ? undefined : "hidden",
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

type Sections = {
  // Topmost: a single in-flight or ready-for-checkpoint scenario.
  // We surface only one as Continue — picking ready_check first
  // because it's the higher-priority moment (per Phase 5 review).
  continueScenario: ScenarioSummary | undefined;
  available: ScenarioSummary[];
  inProgress: ScenarioSummary[];
  locked: ScenarioSummary[];
  // NEEDS_REVIEW lives in its own section ("Освежить") between
  // available and completed — it's "do this now" priority but the
  // tone is gentler than ready-for-checkpoint.
  needsReview: ScenarioSummary[];
  completed: ScenarioSummary[];
};

function groupByStatus(all: ScenarioSummary[]): Sections {
  // Order each group internally by stage then slug for stability —
  // matches the API's default sort.
  const by = (status: string) =>
    all
      .filter((s) => s.progress?.status === status)
      .toSorted((a, b) => a.stage - b.stage || a.slug.localeCompare(b.slug));

  const ready = by("READY_FOR_CHECKPOINT");
  const inProgress = by("IN_PROGRESS");
  // Continue priority: ready_check first (kullmination), then
  // in_progress. The other one (if any) drops into the regular
  // section so it's still visible.
  const continueScenario = ready[0] ?? inProgress[0];

  return {
    continueScenario,
    available: by("AVAILABLE"),
    inProgress: inProgress.filter((s) => s !== continueScenario),
    locked: by("LOCKED"),
    needsReview: by("NEEDS_REVIEW"),
    completed: by("COMPLETED"),
  };
}

function Sections({ sections, onLockedTap }: { sections: Sections; onLockedTap: (s: ScenarioSummary) => void }) {
  const t = useT();
  const groups: Array<{
    title: string;
    items: ScenarioSummary[];
    onClick?: (s: ScenarioSummary) => void;
  }> = [
    { title: t("skills.section_in_progress"), items: sections.inProgress },
    { title: t("skills.section_needs_review"), items: sections.needsReview },
    { title: t("skills.section_available"), items: sections.available },
    { title: t("skills.section_locked"), items: sections.locked, onClick: onLockedTap },
    { title: t("skills.section_completed"), items: sections.completed },
  ];

  return (
    <>
      {groups
        .filter((g) => g.items.length > 0)
        .map((g) => (
          <section key={g.title}>
            <div className="mb-2 px-0.5 text-[11px] font-semibold uppercase tracking-wider text-text-3">{g.title}</div>
            <div className="flex flex-col gap-2">
              {g.items.map((s) => (
                <ScenarioRow key={s.slug} scenario={s} onClick={g.onClick ? () => g.onClick!(s) : undefined} />
              ))}
            </div>
          </section>
        ))}
    </>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-[var(--r-lg)] border border-dashed border-border p-6 text-center text-[13px] text-text-3">
      {message}
    </div>
  );
}
